'use client';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useCurrency } from 'contexts/currency-context';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

interface CurrencySwitcherProps {
  variant?: 'mobile' | 'desktop';
}

export function CurrencySwitcher({ variant = 'mobile' }: CurrencySwitcherProps) {
  const { selectedCurrency, setCurrency, currencies } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);

  if (variant === 'desktop') {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="h-auto p-2 hover:bg-gray-100 min-w-0"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative h-[18px] w-[18px] overflow-hidden rounded-full flex-shrink-0">
                <Image
                  src={selectedCurrency.flag}
                  alt={`${selectedCurrency.code} flag`}
                  fill
                  className="object-cover"
                />
              </div>
              <span className="text-xs text-[#495057] truncate">
                {selectedCurrency.name}
              </span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-[#495057] flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-[#495057] flex-shrink-0" />
              )}
            </div>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="absolute top-full right-0 z-50 mt-2 min-w-[160px] max-w-[240px] space-y-1 rounded-sm bg-[#FCFCFC] p-2 shadow-[1px_2px_10px_0px_rgba(204,210,217,0.8)] border">
            {currencies.map((currency) => (
              <Button
                key={currency.id}
                variant="ghost"
                className="h-auto w-full justify-start p-2 hover:bg-gray-50 text-left"
                onClick={() => {
                  setCurrency(currency);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="relative h-[18px] w-[18px] overflow-hidden rounded-full flex-shrink-0">
                    <Image
                      src={currency.flag}
                      alt={`${currency.code} flag`}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <span className="text-xs text-[#495057] truncate">
                    {currency.name}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="border-b p-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="h-auto w-full justify-between p-2 text-left hover:bg-gray-100"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="relative h-[18px] w-[18px] overflow-hidden rounded-full flex-shrink-0">
                <Image
                  src={selectedCurrency.flag}
                  alt={`${selectedCurrency.code} flag`}
                  fill
                  className="object-cover"
                />
              </div>
              <span className="text-xs text-[#495057] truncate">
                {selectedCurrency.name}
              </span>
            </div>
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-[#495057] flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[#495057] flex-shrink-0" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-1 rounded-sm bg-[#FCFCFC] p-2 shadow-[1px_2px_10px_0px_rgba(204,210,217,0.8)] border">
            {currencies.map((currency) => (
              <Button
                key={currency.id}
                variant="ghost"
                className="h-auto w-full justify-start p-2 hover:bg-gray-50 text-left"
                onClick={() => {
                  setCurrency(currency);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="relative h-[18px] w-[18px] overflow-hidden rounded-full flex-shrink-0">
                    <Image
                      src={currency.flag}
                      alt={`${currency.code} flag`}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <span className="text-xs text-[#495057] truncate">
                    {currency.name}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}